/**
 * CLIENT-SIDE drill-down tree builder — the "merged Drill-Down + Pivot" engine.
 *
 * Turns FLAT dataset rows (the flat compute contract, which can join columns
 * from several datasets) into the SAME tree shape the backend's drill-down
 * compute returns (`{ label, level, levelLabel, measures: {...}, children }` +
 * a `total` object), so everything downstream — buildDrillDownWidgetData,
 * lib-drill-down (expand/collapse, editable cells, year filter, visuals,
 * export), the save pipeline and drilldown-save.utils — works unchanged.
 *
 * On top of the classic hierarchy it adds the pivot-table capabilities:
 *  - PIVOT COLUMN DIMENSIONS: every measure fans out per distinct column value
 *    (e.g. fiscal_year → "2024 Net Sales | 2025 Net Sales | …"), with a grand
 *    total per generated column.
 *  - FORMULA (computed) COLUMNS: evaluated per node on that node's AGGREGATED
 *    measures — so a ratio like Margin % = Profit / Revenue is correct at every
 *    level (parents divide sums, they don't average children's ratios).
 *  - Per-measure aggregation (SUM / AVG / COUNT / MIN / MAX) over the raw rows
 *    under a node — a parent's AVG is the average of ALL rows beneath it.
 *  - `valueOrder` on any level / column dimension fixes the display order.
 *
 * Pure: no Angular dependency. The formula engine is injected as a factory
 * (`SelfServiceFormulaService.buildRowEvaluator`) so this stays testable.
 */

import { parseNumericText } from './table-cell-config';

type Row = Record<string, unknown>;

export interface DrillClientDimension {
  field: string;
  label?: string;
  /** Optional fixed display order for this dimension's values. */
  valueOrder?: string[];
  /** Owning dataset (informational — rows arrive already joined). */
  dataset?: string;
}

export interface DrillClientMeasure {
  field: string;
  /** Column header / measure key (defaults to the field). */
  label?: string;
  /** SUM (default) | AVG | COUNT | MIN | MAX | NONE (= SUM). */
  aggregation?: string;
  /** Include in the grand-total row. */
  total?: boolean;
  /** Header text of this measure's column INSIDE the right-hand Total block
   *  only (e.g. "Revenue" over the Sales summed across the verticals). The
   *  generated key stays "<Total> <label>" — formulas, edits, exports and the
   *  grand total are untouched; only the rendered header changes. Absent =
   *  the measure's own label, like the other blocks. */
  rowTotalHeader?: string;
  dataset?: string;
  /** With pivot column dimensions: fan this measure out per column value
   *  (default) or — `false` — keep ONE column (an attribute such as
   *  "Cost increase %" or "AOP Target"). */
  pivot?: boolean;
  /** 'text' = a per-row attribute (e.g. "Voice of Business"): rolled up as the
   *  DISTINCT values under the node, comma-joined in first-seen order
   *  ("Partial Increase, Defender Program"; nulls / blanks skipped), never
   *  summed, editable as free text when the measure is flagged editable.
   *  Default 'number'. */
  kind?: 'number' | 'text';
  /** Computed (available to formulas + totals maths) but NOT shown as a column
   *  — e.g. Total Cost feeding SSA % / Margin % without its own column. */
  hidden?: boolean;
  /** 'latest' = aggregate ONLY the rows of the newest year present in the data
   *  (read from `yearField`), so "Sales" is the latest year's sales rather than
   *  every year summed — at every level, per pivot value and in the Total
   *  block alike. Absent = all rows. */
  yearScope?: 'latest';
  /** The column holding the year the scope keys off (e.g. fiscal_year). A
   *  plain year (2026), "FY2026" or a date string all resolve to the year. */
  yearField?: string;
  /** Show the year column itself in the table (default off — the widget
   *  builder strips any header matching `yearField` unless this is set). */
  showYearColumn?: boolean;
  /** The year column itself, carried as a measure (hidden plumbing, or the
   *  visible "Show year column" text column). Its header never gets the
   *  "(2026)" year suffix even though it is year-scoped. */
  yearHelper?: boolean;
}

export interface DrillClientComputedColumn {
  name: string;
  expression: string;
  /** Per-column-value (true) or one column (false). Absent = inferred: split
   *  when the expression references a split measure. */
  split?: boolean;
  /** Display position: the measure (label or field) or earlier formula this
   *  column follows. Absent = after the last measure. */
  after?: string;
}

export interface DrillClientConfig {
  /** Hierarchy levels, outer → inner (Level 1 first). */
  dimensions: DrillClientDimension[];
  /** Pivot: column dimension(s) whose values fan every measure out across. */
  columnDimensions?: DrillClientDimension[];
  measures: DrillClientMeasure[];
  /** Formula columns evaluated per node (and per pivot column value). */
  computedColumns?: DrillClientComputedColumn[];
  /** Explicit display order of the BASE columns (before pivot fan-out) — the
   *  builder's "Column order" list. Entries are a measure's FIELD (rename-proof)
   *  or header text, or a formula's name; matching is case-insensitive. Listed
   *  columns render first in this order; anything unlisted (a column added
   *  after the order was set) keeps its default measure-order / "after"-anchor
   *  placement behind them. Empty/absent = default order. */
  columnOrder?: string[];
  /** Fallback used when `columnOrder` is empty: the config-driven DESIGN
   *  preset (`options.drillColumnOrderPreset` in table-matrix.json). Same
   *  semantics, but each entry may list aliases separated by "|"
   *  ("Qty|Net Quantity|Quantity") and matching ignores case, spaces and
   *  punctuation ("SSA %" = "SSA%"). Only the columns a table actually has are
   *  affected — whichever preset columns exist render in preset order, the
   *  rest keep their default placement behind them. */
  columnOrderPreset?: string[];
  /** Fallback PIVOT COLUMN VALUE order (the "Mining | Marine | Oil & Gas |
   *  Rail" band sequence) used when the first column dimension carries no
   *  explicit `valueOrder`: the config-driven DESIGN preset
   *  (`options.drillPivotValueOrderPreset` in table-matrix.json). Entries are
   *  "name|alias|…" compared ignoring case, spaces and punctuation ("Oil & Gas"
   *  = "oil gas"; "Oil and Gas" only via an alias). Listed values render first
   *  in preset order; unlisted values keep their data order behind them. The
   *  Total block (rowTotal) always stays last. */
  columnValueOrderPreset?: string[];
  /** Leaf level shows ONE node per response row (individual values, nothing
   *  combined) instead of one aggregated node per value. Parents still roll up. */
  rawLeafRows?: boolean;
  /** Two-tier header layout with pivot column dimensions:
   *  - 'measure' (default): the MEASURE spans its column values —
   *    "Sales" over Mining | Rail | Marine, then "Price %" over Mining | Rail | …
   *  - 'column': the COLUMN VALUE spans the measures — "Mining" over
   *    Sales | Price % | Reason, then "Rail" over Sales | Price % | Reason.
   *  Only changes the generated key ORDER (and which label is the band); the
   *  keys themselves ("Mining Sales") are identical in both layouts. */
  headerGroupBy?: 'measure' | 'column';
  /** With pivot column dimensions: add ONE extra column value, `rowTotalLabel`
   *  ("Total"), holding every TOTALLED measure (`total: true` — the same
   *  "Show in Grand Total" tick as the bottom row; numeric only) aggregated
   *  over ALL the node's rows, across the verticals — the Figma's trailing
   *  "TOTAL | Rev | Price % | Impact" block. A split FORMULA column joins it
   *  too when every measure it references is totalled (the same rule that
   *  admits it to the bottom total row), evaluated on the block's totals —
   *  total Rev × total Price %, exactly like the bottom row. Text attributes
   *  stay out. Renders as the last block with headerGroupBy 'column', or as
   *  a last "Total" sub-column per measure with 'measure'. */
  rowTotal?: boolean;
  /** Column-value label of the row-total block. Default "Total". */
  rowTotalLabel?: string;
  /** WHERE the row-total block sits (the Totals picker's "… and left" /
   *  "Left only" placements → `drillTotalsBlockPosition`):
   *  'right' (default) = trailing, after the last pivot column value;
   *  'left' = directly after the first-level (tree) column, ahead of every
   *  other measure column — headerGroupBy 'column' puts the whole "Total"
   *  block first, 'measure' puts the "Total" sub-column first inside every
   *  measure's block. Only the generated key ORDER changes (and the order of
   *  `columnValues`); keys, totals, formulas and editing are untouched. */
  rowTotalPosition?: 'left' | 'right';
  /** WHICH of the totalled columns the row-total block shows — the design's
   *  "TOTAL | Revenue | Price % | Impact" block (config
   *  `options.drillRowTotalColumnsPreset` in table-matrix.json). "name|alias|…"
   *  entries matched against a measure's header, field or `rowTotalHeader`, or
   *  a formula column's name, ignoring case, spaces and punctuation ("Price %"
   *  = "Price Change %" via an alias). Empty/absent = every totalled measure
   *  and every qualifying formula joins. The bottom grand-total row is NOT
   *  affected. */
  rowTotalColumns?: string[];
  /** `false` = no grand-total row (tree.total = null) even when measures are
   *  ticked `total` — the Totals picker's "Right only" / "None". The ticks
   *  still decide which measures the row-total block carries. Default true. */
  grandTotalRow?: boolean;
}

/** Where a drill-down shows its totals (build-form "Totals" picker):
 *  bottom = grand-total row only; right / left = the Total block across the
 *  pivot column values only (trailing / right after the first-level column);
 *  both / both-left = row + block; none = neither. */
export type DrillTotalsPlacement = 'none' | 'bottom' | 'right' | 'both' | 'left' | 'both-left';
export function normalizeDrillTotalsPlacement(value: unknown): DrillTotalsPlacement {
  return value === 'none' || value === 'bottom' || value === 'right' || value === 'both' || value === 'left' || value === 'both-left'
    ? value
    : 'both';
}
export function drillTotalsAtBottom(placement: DrillTotalsPlacement): boolean {
  return placement === 'bottom' || placement === 'both' || placement === 'both-left';
}
/** The Total block is on (either side). */
export function drillTotalsAtRight(placement: DrillTotalsPlacement): boolean {
  return placement === 'right' || placement === 'both' || placement === 'left' || placement === 'both-left';
}
/** Which side the Total block sits on for a placement that has one. */
export function drillTotalsBlockPosition(placement: DrillTotalsPlacement): 'left' | 'right' {
  return placement === 'left' || placement === 'both-left' ? 'left' : 'right';
}
/** `SelfServiceFormulaService.buildRowEvaluator`-compatible factory. */
export type RowEvaluatorFactory = (
  expression: string,
  columnNames: string[],
) => (row: Record<string, string>) => unknown;

export interface DrillClientTreeNode {
  label: string;
  level: number;
  levelLabel: string;
  measures: Record<string, number | string | null>;
  children: DrillClientTreeNode[];
}

export interface DrillClientTree {
  data: DrillClientTreeNode[];
  /** Grand total per measure key — null when NO measure is flagged `total`
   *  (matches the backend, which then omits the total row). */
  total: Record<string, number | string | null> | null;
  /** Measure keys BEFORE pivot fan-out (the labels), in config order. */
  baseMeasureKeys: string[];
  /** Pivot column values in display order ("" when no column dimension). */
  columnValues: string[];
  /** Every generated measure/computed key, in column order. */
  measureKeys: string[];
  /** Generated keys that hold TEXT attributes (render/edit as text). */
  textMeasureKeys: string[];
  /** Base keys (measures + formulas) that are NOT fanned out per column value. */
  unsplitKeys: string[];
  /** The formula columns with their resolved split decision (+ anchor). */
  computed: Array<{ name: string; expression: string; split: boolean; after?: string }>;
  /** Header layout the keys were ordered for (see DrillClientConfig.headerGroupBy);
   *  'column' only when a pivot is in play. */
  headerGroupBy: 'measure' | 'column';
  /** The row-total block's column value ("Total") when the block is on. */
  rowTotalValue?: string;
  /** Base measure key → header text inside the row-total block (measure
   *  `rowTotalHeader`, e.g. Sales → "Revenue"). Only the renamed ones. */
  rowTotalHeaders?: Record<string, string>;
  /** Base measure key → the single year its values are scoped to (measure
   *  `yearScope: 'latest'`), e.g. { Sales: 2026 }. Only the scoped ones. */
  yearScopes?: Record<string, number>;
}

/** Metadata lib-drill-down needs to RE-EVALUATE formula columns after an edit. */
export interface DrillFormulaMeta {
  computedColumns: DrillClientComputedColumn[];
  baseMeasureKeys: string[];
  columnValues: string[];
  /** Extra names an expression may use for a base key (alias → base key),
   *  e.g. the raw dataset field "net_sales" → the header "Net Sales". */
  aliases?: Record<string, string>;
  /** Base keys (measures + formulas) that are NOT fanned out per column value. */
  unsplitKeys?: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────

const normKey = (s: unknown): string => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** Resolve configured field names to the keys that ACTUALLY exist on the rows.
 *  The backend may echo display names ("Fiscal Year") instead of fields
 *  (fiscal_year); a joined column may come back under its displayName. Unions
 *  the keys of the first 200 rows (sparse rows omit null fields). */
function fieldKeyResolver(rows: Row[], aliases: Array<[string, string | undefined]>): (field: string) => string {
  const exact = new Set<string>();
  const byNorm = new Map<string, string>();
  rows.slice(0, 200).forEach((r) => Object.keys(r || {}).forEach((k) => {
    if (!exact.has(k)) {
      exact.add(k);
      const n = normKey(k);
      if (!byNorm.has(n)) byNorm.set(n, k);
    }
  }));
  const aliasOf = new Map<string, string>();
  aliases.forEach(([field, label]) => { if (field && label) aliasOf.set(field, label); });
  return (field: string): string => {
    if (exact.has(field)) return field;
    const viaNorm = byNorm.get(normKey(field));
    if (viaNorm) return viaNorm;
    const label = aliasOf.get(field);
    if (label) {
      if (exact.has(label)) return label;
      const viaLabel = byNorm.get(normKey(label));
      if (viaLabel) return viaLabel;
    }
    return field;
  };
}

function toNum(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') return parseNumericText(value);
  return null;
}

function aggregate(rows: Row[], key: string, mode: string): number | null {
  const upper = String(mode || 'SUM').toUpperCase();
  if (upper === 'COUNT') return rows.length;
  const values = rows.map((r) => toNum(r[key])).filter((n): n is number => n !== null);
  if (!values.length) return null;
  switch (upper) {
    case 'AVG': case 'AVERAGE': return values.reduce((a, b) => a + b, 0) / values.length;
    case 'MIN': return Math.min(...values);
    case 'MAX': return Math.max(...values);
    case 'DISTINCT': return new Set(values).size;
    case 'SUM': case 'NONE': default: return values.reduce((a, b) => a + b, 0);
  }
}

/** Distinct values of a key in first-seen order, then re-ordered by an optional
 *  explicit `valueOrder` (listed values first, in that order; the rest keep
 *  their data order). All-numeric value sets (years) sort ascending. */
function orderedDistinct(rows: Row[], key: string, valueOrder?: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  rows.forEach((r) => {
    const v = String(r[key] ?? '');
    if (!seen.has(v)) { seen.add(v); out.push(v); }
  });
  if (out.length > 1 && out.every((v) => v !== '' && Number.isFinite(Number(v)))) {
    out.sort((a, b) => Number(a) - Number(b));
  }
  const order = (valueOrder || []).map((v) => String(v).trim().toLowerCase()).filter(Boolean);
  if (order.length) {
    const rank = (v: string): number => {
      const i = order.indexOf(v.trim().toLowerCase());
      return i === -1 ? order.length : i;
    };
    out.sort((a, b) => rank(a) - rank(b));
  }
  return out;
}

/** Re-order pivot column values by the design preset ("name|alias|…" entries,
 *  compared via `normalizeColumnName`): listed values first in preset order,
 *  the rest keep their incoming order behind them. Stable; no preset → as is. */
export function applyColumnValueOrderPreset(values: string[], preset?: string[]): string[] {
  const explicit: string[][] = (preset || [])
    .map((entry) => String(entry ?? '').split('|').map(normalizeColumnName).filter(Boolean))
    .filter((aliases) => aliases.length);
  if (!explicit.length || values.length < 2) return values;
  const rank = (v: string): number => {
    const key = normalizeColumnName(v);
    const i = explicit.findIndex((aliases) => aliases.includes(key));
    return i === -1 ? explicit.length : i;
  };
  return values
    .map((value, i) => ({ value, i }))
    .sort((a, b) => rank(a.value) - rank(b.value) || a.i - b.i)
    .map((entry) => entry.value);
}

/** Predicate for the row-total block's column list (`rowTotalColumns`,
 *  "name|alias|…" entries via `normalizeColumnName`): does this measure —
 *  by header, field or its `rowTotalHeader` — appear in the list? An empty /
 *  absent list admits every measure (the caller's own rules still apply). */
export function rowTotalColumnMatcher(preset?: string[]): (measure: DrillClientMeasure) => boolean {
  const byName = rowTotalNameMatcher(preset);
  return (m) => byName([drillMeasureKey(m), String(m.field ?? ''), String(m.rowTotalHeader ?? '')]);
}

/** Name-level form of `rowTotalColumnMatcher`: do ANY of the candidate names
 *  (a measure's header / field / rowTotalHeader, or a formula column's name)
 *  appear in the preset? Empty / absent preset admits everything. */
export function rowTotalNameMatcher(preset?: string[]): (names: string[]) => boolean {
  const allowed = new Set<string>(
    (preset || []).flatMap((entry) => String(entry ?? '').split('|').map(normalizeColumnName).filter(Boolean)),
  );
  if (!allowed.size) return () => true;
  return (names) => names.map(normalizeColumnName).some((candidate) => candidate && allowed.has(candidate));
}

/** The measure key (column header) a measure renders under. */
export function drillMeasureKey(measure: { field: string; label?: string }): string {
  return String(measure.label || measure.field || '').trim() || String(measure.field || '');
}

/** "<column value> <measure key>" — the generated key of a pivoted measure. */
export function pivotedKey(columnValue: string, baseKey: string): string {
  return `${columnValue || '(blank)'} ${baseKey}`;
}

/** Column-name comparison key for the Column-order list / preset: lower-case,
 *  letters and digits only ("Realized Cost Increase" = "realized cost increase"
 *  = "Realized-Cost-Increase"; "SSA %" = "SSA%"). */
export function normalizeColumnName(name: string): string {
  return String(name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** One BASE output column in display order: a measure (index into the
 *  measures list) or a formula (index into the computed columns), with the
 *  header text it renders under (before any pivot fan-out). */
export interface DrillOrderItem { kind: 'measure' | 'formula'; index: number; key: string }

/** Resolve the display order of the BASE columns: measures in config order,
 *  each formula right after its `after` anchor (a measure's header/field or an
 *  earlier formula; queued behind formulas already anchored there) else at the
 *  end — then the explicit `columnOrder` on top (stable: unlisted items keep
 *  that default relative order after the listed ones). Shared by the tree
 *  builder AND the builder's "Column order" list so the list IS the table. */
export function resolveDrillColumnOrder(
  measures: Array<{ field: string; label?: string }>,
  computedColumns: Array<{ name: string; after?: string }>,
  columnOrder?: string[],
  columnOrderPreset?: string[],
): DrillOrderItem[] {
  const baseMeasureKeys = measures.map(drillMeasureKey);
  const order: DrillOrderItem[] = measures.map((_m, index) => ({ kind: 'measure' as const, index, key: baseMeasureKeys[index] }));
  const normalizedAfter = (c: { after?: string }): string => String(c.after || '').trim().toLowerCase();
  computedColumns.forEach((c, index) => {
    const after = normalizedAfter(c);
    let at = -1;
    if (after) {
      const mi = measures.findIndex((m, i) => baseMeasureKeys[i].toLowerCase() === after || String(m.field).toLowerCase() === after);
      const anchor = mi >= 0 ? baseMeasureKeys[mi].toLowerCase() : after;
      at = order.findIndex((item) => item.key.toLowerCase() === anchor);
      // Queue behind formulas already anchored to the same column (keeps their
      // config order instead of reversing it).
      while (at >= 0 && at + 1 < order.length && order[at + 1].kind === 'formula' && normalizedAfter(computedColumns[order[at + 1].index]) === after) at++;
    }
    const item: DrillOrderItem = { kind: 'formula', index, key: String(c.name) };
    if (at >= 0) order.splice(at + 1, 0, item);
    else order.push(item);
  });
  // Explicit list first; else the design preset. Each entry = "name|alias|…";
  // names are compared ignoring case, whitespace and punctuation so "SSA %",
  // "SSA%" and "ssa %" are one column.
  const source = (columnOrder || []).some((k) => String(k ?? '').trim()) ? columnOrder || [] : columnOrderPreset || [];
  const explicit: string[][] = source
    .map((entry) => String(entry ?? '').split('|').map(normalizeColumnName).filter(Boolean))
    .filter((aliases) => aliases.length);
  if (!explicit.length) return order;
  const rank = (item: DrillOrderItem): number => {
    const candidates = [normalizeColumnName(item.key)];
    if (item.kind === 'measure') candidates.push(normalizeColumnName(String(measures[item.index].field ?? '')));
    const i = explicit.findIndex((aliases) => aliases.some((alias) => candidates.includes(alias)));
    return i === -1 ? explicit.length : i;
  };
  return order
    .map((item, i) => ({ item, i }))
    .sort((a, b) => rank(a.item) - rank(b.item) || a.i - b.i)
    .map((entry) => entry.item);
}

/** Build one row-evaluator per computed column (in order, so later formulas can
 *  reference earlier ones). Broken expressions are skipped. */
type CompiledFormula = { name: string; expression: string; split?: boolean; after?: string; evaluate: (row: Record<string, string>) => unknown; names: string[] };
function compileComputed(
  computed: DrillClientComputedColumn[],
  knownNames: string[],
  factory: RowEvaluatorFactory | undefined,
): CompiledFormula[] {
  if (!factory) return [];
  const out: CompiledFormula[] = [];
  const names = [...knownNames];
  computed.forEach((c) => {
    if (!c?.name || !c?.expression) return;
    try {
      const evaluate = factory(c.expression, [...names]);
      out.push({ name: c.name, expression: c.expression, split: c.split, after: c.after, evaluate, names: [...names] });
      names.push(c.name);
    } catch {
      // Skip a broken formula — its column just won't be present.
    }
  });
  return out;
}

function evalToCell(value: unknown): number | string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : String(value);
}

/** Evaluate every compiled formula on a bag of base values, writing each result
 *  back into `target` under `keyFor(name)`. The formula row exposes BOTH the
 *  measure key (label) AND the raw field name for every base measure, so
 *  `[Net Sales]` and `net_sales` both resolve. */
function applyComputedToBag(
  compiled: CompiledFormula[],
  bag: Record<string, unknown>,
  target: Record<string, unknown>,
  keyFor: (name: string) => string,
): void {
  if (!compiled.length) return;
  const row: Record<string, string> = {};
  Object.keys(bag).forEach((k) => { row[k] = bag[k] === null || bag[k] === undefined ? '' : String(bag[k]); });
  compiled.forEach((c) => {
    let value: number | string | null = null;
    try { value = evalToCell(c.evaluate(row)); } catch { value = null; }
    target[keyFor(c.name)] = value;
    row[c.name] = value === null ? '' : String(value);
  });
}

/** Text attribute roll-up for the rows under a node: every DISTINCT non-blank
 *  value in first-seen order, joined with ", " — so a parent, or a pivot cell
 *  spanning many raw rows, reads "Partial Increase, Defender Program" instead
 *  of going blank the moment two rows disagree. (The previous single-agreed-
 *  value rule blanked almost every cell of a per-transaction attribute such as
 *  pricing action reason.) One agreed value renders as itself; all null → null. */
function textValue(rows: Row[], key: string): string | null {
  const seen = new Set<string>();
  for (const r of rows) {
    const v = r[key];
    if (v === null || v === undefined) continue;
    const s = String(v).trim();
    if (s === '') continue;
    seen.add(s);
  }
  return seen.size ? Array.from(seen).join(', ') : null;
}

/** Column names an expression references — `[Bracketed Names]` plus bare
 *  identifiers — lower-cased, for the split / unsplit decision. */
function referencedNames(expression: string): string[] {
  const out: string[] = [];
  const bracket = /\[([^\]]+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = bracket.exec(expression)) !== null) out.push(m[1].trim().toLowerCase());
  const bare = expression.replace(/\[[^\]]+\]/g, ' ').match(/[A-Za-z_][A-Za-z0-9_]*/g) || [];
  bare.forEach((b) => out.push(b.toLowerCase()));
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Tree build
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build the drill-down tree (+ grand total) from flat rows.
 * Returns an EMPTY tree (data: []) when the config is incomplete.
 */
export function buildDrillDownTreeFromRows(
  rows: Row[],
  config: DrillClientConfig,
  evaluatorFactory?: RowEvaluatorFactory,
): DrillClientTree {
  const safeRows = Array.isArray(rows) ? rows.filter((r) => r && typeof r === 'object') : [];
  const dims = (config.dimensions || []).filter((d) => d && d.field);
  const colDims = (config.columnDimensions || []).filter((d) => d && d.field);
  const measures = (config.measures || []).filter((m) => m && m.field);
  const computed = (config.computedColumns || []).filter((c) => c && c.name && c.expression);
  const baseMeasureKeys = measures.map(drillMeasureKey);

  if (!dims.length || !measures.length || !safeRows.length) {
    return { data: [], total: null, baseMeasureKeys, columnValues: [], measureKeys: [], textMeasureKeys: [], unsplitKeys: [], computed: [], headerGroupBy: 'measure' };
  }

  const resolve = fieldKeyResolver(safeRows, [
    ...dims.map((d): [string, string | undefined] => [d.field, d.label]),
    ...colDims.map((d): [string, string | undefined] => [d.field, d.label]),
    ...measures.map((m): [string, string | undefined] => [m.field, m.label]),
  ]);
  const dimKeys = dims.map((d) => resolve(d.field));
  const colKeys = colDims.map((d) => resolve(d.field));
  const measureKeysR = measures.map((m) => resolve(m.field));

  // Pivot column values: the joined distinct combination of the column
  // dimensions' values (single column dim → its plain values). Ordered by
  // valueOrder (first column dim) / numeric-ascending (years) / data order.
  const hasPivot = colKeys.length > 0;
  const colValueOf = (r: Row): string => colKeys.map((k) => String(r[k] ?? '')).join(' / ');
  let columnValues: string[] = [];
  if (hasPivot) {
    if (colKeys.length === 1) {
      columnValues = orderedDistinct(safeRows, colKeys[0], colDims[0].valueOrder);
    } else {
      const seen = new Set<string>();
      safeRows.forEach((r) => { const k = colValueOf(r); if (!seen.has(k)) { seen.add(k); columnValues.push(k); } });
    }
    // No explicit value order on the tile → the design preset decides the
    // band sequence (Mining | Marine | Oil & Gas | Rail); data order otherwise.
    const explicitOrder = (colDims[0].valueOrder || []).some((v) => String(v ?? '').trim());
    if (!explicitOrder) columnValues = applyColumnValueOrderPreset(columnValues, config.columnValueOrderPreset);
  }

  // Formula engine: known names = measure keys + raw measure fields (+ earlier
  // computed names, added as they compile).
  const knownNames = Array.from(new Set([...baseMeasureKeys, ...measures.map((m) => m.field)]));
  const compiled = compileComputed(computed, knownNames, evaluatorFactory);
  const computedNames = compiled.map((c) => c.name);

  // Which measures fan out per pivot column value: all of them by default;
  // `pivot: false` keeps ONE column (an attribute such as "Cost increase %" or
  // a free-text "Voice of Business"). Text measures roll up as the distinct
  // values under the node, comma-joined (see textValue).
  const isSplit = (m: DrillClientMeasure): boolean => hasPivot && m.pivot !== false;
  const isText = (m: DrillClientMeasure): boolean => m.kind === 'text';
  // Year scope ('latest'): a scoped measure aggregates only the rows of the
  // NEWEST year found anywhere in the data for its year column — one year for
  // the whole table (so every node, pivot slice and the Total block agree),
  // never "each node's own latest year". The year column reaches the flat rows
  // because the host requests it alongside the measures (builder compute
  // payload / a hidden helper measure in the saved payload).
  const yearOf = (v: unknown): number | null => {
    const n = toNum(v);
    if (n !== null && n >= 1900 && n <= 2200) return Math.floor(n);
    const match = String(v ?? '').match(/(?:19|20)\d{2}/);
    return match ? Number(match[0]) : null;
  };
  const yearKeyR = measures.map((m) => (m.yearScope === 'latest' && String(m.yearField || '').trim() ? resolve(String(m.yearField).trim()) : null));
  const latestYear: Array<number | null> = yearKeyR.map((k) => {
    if (!k) return null;
    let max: number | null = null;
    for (const r of safeRows) {
      const y = yearOf(r[k]);
      if (y !== null && (max === null || y > max)) max = y;
    }
    return max;
  });
  const scopeRows = (i: number, rows: Row[]): Row[] => {
    const k = yearKeyR[i];
    const y = latestYear[i];
    return k && y !== null ? rows.filter((r) => yearOf(r[k]) === y) : rows;
  };
  /** Base measure key → the year its values are scoped to (reported so the
   *  header can say "Latest year: 2026"). */
  const yearScopes: Record<string, number> = {};
  measures.forEach((m, i) => { const y = latestYear[i]; if (y !== null && !m.yearHelper) yearScopes[baseMeasureKeys[i]] = y; });
  const valueFor = (m: DrillClientMeasure, i: number, rows: Row[]): number | string | null => {
    const scoped = scopeRows(i, rows);
    return scoped.length ? (isText(m) ? textValue(scoped, measureKeysR[i]) : aggregate(scoped, measureKeysR[i], m.aggregation || 'SUM')) : null;
  };
  // A formula splits per column value when it references a split measure (or a
  // split formula); one built only from unsplit attributes stays one column.
  const splitNames = new Set<string>();
  measures.filter(isSplit).forEach((m) => { splitNames.add(drillMeasureKey(m).toLowerCase()); splitNames.add(String(m.field).toLowerCase()); });
  const formulaSplit = compiled.map((c) => {
    const split = hasPivot && (c.split ?? referencedNames(c.expression).some((n) => splitNames.has(n)));
    if (split) splitNames.add(c.name.toLowerCase());
    return split;
  });
  const unsplitCompiled = compiled.filter((_c, i) => !formulaSplit[i]);

  // ROW TOTAL across the pivot column values: one extra column value ("Total")
  // per split numeric measure / split formula. It is a normal generated key
  // ("Total Sales"), so headers, bands, grand total and edits treat it like
  // any other column value; `outputCvs` is the column-value list every key
  // derivation below uses (and what the tree reports as its columnValues).
  // WHICH columns join the block: the measures ticked "Show in Grand Total"
  // (`total`), numeric only — the one tick drives the bottom row AND the right
  // block. A split FORMULA joins when every measure it references is ticked
  // (the bottom-row rule — Impact = Sales × Price % with both totalled), its
  // value in the block being the formula over the block's totals, exactly as
  // in the bottom row. Nothing ticked → no block at all. On top,
  // `rowTotalColumns` (the design preset: Revenue | Price % | Impact) narrows
  // the block to the listed measures / formulas only; the bottom total row
  // still carries every tick.
  const inRowTotalColumns = rowTotalColumnMatcher(config.rowTotalColumns);
  const inRowTotalNames = rowTotalNameMatcher(config.rowTotalColumns);
  const measureInRowTotal = (m: DrillClientMeasure): boolean =>
    isSplit(m) && !m.hidden && !isText(m) && m.total === true && inRowTotalColumns(m);
  const rowTotal = hasPivot && config.rowTotal === true && measures.some(measureInRowTotal);
  // Formula eligibility, in compile order so a formula may build on an earlier
  // qualifying one. Inputs only need the TICK (a hidden or preset-excluded
  // input is still aggregated into the Total slice); the preset gates the
  // formula itself by name.
  const totalledLower = new Set<string>();
  measures.filter((m) => m.total === true && !isText(m)).forEach((m) => { totalledLower.add(drillMeasureKey(m).toLowerCase()); totalledLower.add(String(m.field).toLowerCase()); });
  const anyMeasureLower = new Set<string>();
  measures.forEach((m) => { anyMeasureLower.add(drillMeasureKey(m).toLowerCase()); anyMeasureLower.add(String(m.field).toLowerCase()); });
  const formulaLower = new Set(compiled.map((c) => c.name.toLowerCase()));
  const rowTotalFormulaLower = new Set<string>();
  const formulaInRowTotal: boolean[] = compiled.map((c, i) => {
    if (!rowTotal || !formulaSplit[i]) return false;
    const refs = referencedNames(c.expression).filter((n) => anyMeasureLower.has(n) || formulaLower.has(n));
    const ok = refs.length > 0 && refs.every((n) => totalledLower.has(n) || rowTotalFormulaLower.has(n)) && inRowTotalNames([c.name]);
    if (ok) rowTotalFormulaLower.add(c.name.toLowerCase());
    return ok;
  });
  const totalCv = String(config.rowTotalLabel ?? '').trim() || 'Total';
  // Position: the block trails the verticals (default) or leads them ('left' —
  // right after the first-level column); the column-value list is reported in
  // that same display order.
  const rowTotalLeft = rowTotal && config.rowTotalPosition === 'left';
  const outputCvs = rowTotal && !columnValues.includes(totalCv) ? (rowTotalLeft ? [totalCv, ...columnValues] : [...columnValues, totalCv]) : columnValues;
  // Per-measure header text inside the block ("Revenue" over the summed Sales)
  // — display only; the key stays "<Total> <label>".
  const rowTotalHeaders: Record<string, string> = {};
  measures.forEach((m, i) => {
    const header = String(m.rowTotalHeader ?? '').trim();
    if (rowTotal && header && measureInRowTotal(m)) rowTotalHeaders[baseMeasureKeys[i]] = header;
  });
  /** Column values a split column fans out to (+ the Total when it joins the block). */
  const cvsFor = (inRowTotal: boolean): string[] => (rowTotal && inRowTotal ? outputCvs : columnValues);

  // DISPLAY ORDER: measures in config order, each formula after its `after`
  // anchor (else at the end), and an explicit `columnOrder` (the builder's
  // Column-order list) on top — see resolveDrillColumnOrder.
  type OrderItem = DrillOrderItem;
  const order = resolveDrillColumnOrder(measures, compiled, config.columnOrder, config.columnOrderPreset);
  const keysFor = (item: OrderItem): string[] => {
    if (item.kind === 'measure') {
      if (measures[item.index].hidden) return []; // formula input only
      const base = baseMeasureKeys[item.index];
      return isSplit(measures[item.index]) ? cvsFor(measureInRowTotal(measures[item.index])).map((cv) => pivotedKey(cv, base)) : [base];
    }
    const c = compiled[item.index];
    return formulaSplit[item.index] ? cvsFor(formulaInRowTotal[item.index]).map((cv) => pivotedKey(cv, c.name)) : [c.name];
  };
  // HEADER LAYOUT (headerGroupBy):
  //  'measure' (default) — measure-major: every split column's values sit
  //    together ("Sales": Mining|Rail|Marine, then "Price %": Mining|Rail|…).
  //  'column' — column-value-major: one block per column value holding every
  //    split column ("Mining": Sales|Price %|Reason, then "Rail": Sales|…), so
  //    the band above the header reads Mining | Rail | Marine. Unsplit columns
  //    listed BEFORE the first split one lead the table, the rest trail it, so
  //    no lone attribute breaks a block apart.
  const headerGroupBy: 'measure' | 'column' = hasPivot && config.headerGroupBy === 'column' ? 'column' : 'measure';
  const isSplitItem = (item: OrderItem): boolean =>
    item.kind === 'measure' ? !measures[item.index].hidden && isSplit(measures[item.index]) : formulaSplit[item.index];
  const baseKeyFor = (item: OrderItem): string => (item.kind === 'measure' ? baseMeasureKeys[item.index] : compiled[item.index].name);
  let orderedKeys: string[];
  if (headerGroupBy === 'column') {
    const firstSplit = order.findIndex(isSplitItem);
    const leading = firstSplit < 0 ? order : order.slice(0, firstSplit);
    const rest = firstSplit < 0 ? [] : order.slice(firstSplit);
    const splitItems = rest.filter(isSplitItem);
    const trailing = rest.filter((item) => !isSplitItem(item));
    // The "Total" block carries the totalled measures + the qualifying formulas.
    const inRowTotalItem = (item: OrderItem): boolean =>
      item.kind === 'measure' ? measureInRowTotal(measures[item.index]) : formulaInRowTotal[item.index];
    const itemsFor = (cv: string): OrderItem[] => (cv === totalCv && rowTotal ? splitItems.filter(inRowTotalItem) : splitItems);
    const blockKeys = (cv: string): string[] => itemsFor(cv).map((item) => pivotedKey(cv, baseKeyFor(item)));
    // 'left': the Total block comes FIRST — directly after the tree column,
    // ahead even of the leading unsplit attributes.
    orderedKeys = rowTotalLeft
      ? [
          ...blockKeys(totalCv),
          ...leading.flatMap(keysFor),
          ...outputCvs.filter((cv) => cv !== totalCv).flatMap(blockKeys),
          ...trailing.flatMap(keysFor),
        ]
      : [
          ...leading.flatMap(keysFor),
          ...outputCvs.flatMap(blockKeys),
          ...trailing.flatMap(keysFor),
        ];
  } else {
    orderedKeys = order.flatMap(keysFor);
  }

  /** Aggregate every measure (+ formulas) for a set of rows into `target`.
   *  Values are computed into a scratch bag first (formulas need their inputs
   *  ready), then written in DISPLAY order — insertion order matters because
   *  lib-drill-down derives its column order from the first-seen keys. */
  const fillMeasures = (target: Record<string, number | string | null>, groupRows: Row[]): void => {
    const vals: Record<string, number | string | null> = {};
    const unsplitValues = measures.map((m, i) => (isSplit(m) ? null : valueFor(m, i, groupRows)));
    const unsplitBag: Record<string, unknown> = {};
    measures.forEach((m, i) => {
      if (!isSplit(m)) { unsplitBag[baseMeasureKeys[i]] = unsplitValues[i]; unsplitBag[m.field] = unsplitValues[i]; }
    });
    // One slice per column value — plus, with the row total, a slice over ALL
    // the node's rows whose values are the measures aggregated across the
    // verticals (SUM sums them, AVG averages every row, …) and whose formulas
    // are evaluated on those totals.
    const slices = outputCvs.map((cv) => {
      const isTotalSlice = rowTotal && cv === totalCv;
      const rows = isTotalSlice ? groupRows : groupRows.filter((r) => colValueOf(r) === cv);
      const values = measures.map((m, i) => (isSplit(m) ? valueFor(m, i, rows) : unsplitValues[i]));
      const bag: Record<string, unknown> = { ...unsplitBag };
      measures.forEach((m, i) => {
        if (isSplit(m)) { bag[baseMeasureKeys[i]] = values[i]; bag[m.field] = values[i]; }
      });
      return { cv, values, bag, isTotalSlice };
    });
    measures.forEach((m, i) => {
      if (isSplit(m)) slices.forEach((s) => { if (!s.isTotalSlice || measureInRowTotal(m)) vals[pivotedKey(s.cv, baseMeasureKeys[i])] = s.values[i]; });
      else vals[baseMeasureKeys[i]] = unsplitValues[i];
    });
    // Unsplit formulas first (a split one may reference them), then per slice.
    unsplitCompiled.forEach((c) => {
      applyComputedToBag([c], unsplitBag, vals, (base) => base);
      unsplitBag[c.name] = vals[c.name];
      slices.forEach((s) => { s.bag[c.name] = vals[c.name]; });
    });
    slices.forEach((s) => {
      // Per column value; on the Total slice only the qualifying formulas,
      // evaluated over the block's totals (like the bottom grand-total row).
      compiled.forEach((c, i) => {
        if (!formulaSplit[i]) return;
        if (s.isTotalSlice && !formulaInRowTotal[i]) return;
        applyComputedToBag([c], s.bag, vals, (base) => pivotedKey(s.cv, base));
        s.bag[c.name] = vals[pivotedKey(s.cv, c.name)];
      });
    });
    orderedKeys.forEach((key) => { if (key in vals) target[key] = vals[key]; });
  };

  const walk = (groupRows: Row[], level: number): DrillClientTreeNode[] => {
    const dim = dims[level];
    const key = dimKeys[level];
    const levelLabel = String(dim.label || dim.field);
    const leaf = level + 1 >= dims.length;
    const makeNode = (value: string, nodeRows: Row[]): DrillClientTreeNode => {
      const node: DrillClientTreeNode = {
        label: value === '' ? '(blank)' : value,
        level: level + 1,
        levelLabel,
        measures: {},
        children: [],
      };
      fillMeasures(node.measures, nodeRows);
      if (!leaf) node.children = walk(nodeRows, level + 1);
      return node;
    };
    return orderedDistinct(groupRows, key, dim.valueOrder).flatMap((value) => {
      const nodeRows = groupRows.filter((r) => String(r[key] ?? '') === value);
      // Raw leaf mode: every response row becomes its own leaf node (same
      // label, that row's individual values) instead of one aggregated node.
      if (leaf && config.rawLeafRows) return nodeRows.map((r) => makeNode(value, [r]));
      return [makeNode(value, nodeRows)];
    });
  };
  const data = walk(safeRows, 0);

  // Grand total: only measures flagged `total` participate (their formula
  // columns follow). null when nothing is flagged — no total row, like the
  // backend contract.
  let total: Record<string, number | string | null> | null = null;
  if (config.grandTotalRow !== false && measures.some((m) => m.total)) {
    const all: Record<string, number | string | null> = {};
    fillMeasures(all, safeRows);
    const totalBases = new Set(measures.filter((m) => m.total).map(drillMeasureKey));
    // A formula joins the total row only when EVERY measure it references is
    // totalled (a Margin % over totalled sales/cost — yes; an "AOP gap" over
    // untotalled attributes — no). Formulas may also build on kept formulas.
    const keptLower = new Set<string>();
    measures.filter((m) => m.total).forEach((m) => { keptLower.add(drillMeasureKey(m).toLowerCase()); keptLower.add(String(m.field).toLowerCase()); });
    const measureLower = new Set<string>();
    measures.forEach((m) => { measureLower.add(drillMeasureKey(m).toLowerCase()); measureLower.add(String(m.field).toLowerCase()); });
    const totalComputed = new Set<string>();
    compiled.forEach((c) => {
      const refs = referencedNames(c.expression).filter((n) => measureLower.has(n) || totalComputed.has(n));
      if (refs.length && refs.every((n) => keptLower.has(n) || totalComputed.has(n))) {
        totalComputed.add(c.name.toLowerCase());
        keptLower.add(c.name.toLowerCase());
      }
    });
    const keep = (key: string): boolean => {
      const base = hasPivot ? stripColumnValue(key, outputCvs) : key;
      return totalBases.has(base) || (computedNames.includes(base) && totalComputed.has(base.toLowerCase()));
    };
    total = {};
    Object.keys(all).forEach((k) => { if (keep(k)) (total as Record<string, number | string | null>)[k] = all[k]; });
  }

  // Generated key order = the display order resolved above (split blocks sit
  // inline; formulas follow their `after` anchor or the last measure).
  const measureKeys: string[] = [...orderedKeys];
  const textMeasureKeys = measureKeys.filter((key) => {
    const i = baseMeasureKeys.indexOf(stripColumnValue(key, outputCvs));
    return i >= 0 && isText(measures[i]);
  });
  const unsplitKeys = [
    ...measures.filter((m) => !isSplit(m)).map(drillMeasureKey),
    ...compiled.filter((_c, i) => !formulaSplit[i]).map((c) => c.name),
  ];

  return {
    data,
    total,
    baseMeasureKeys,
    // Includes the row-total label when the row total is on, so hosts split
    // "Total Sales" into column value + base exactly like "Mining Sales".
    columnValues: outputCvs,
    measureKeys,
    textMeasureKeys,
    unsplitKeys,
    computed: compiled.map((c, i) => ({ name: c.name, expression: c.expression, split: formulaSplit[i], ...(c.after ? { after: c.after } : {}) })),
    headerGroupBy,
    ...(rowTotal ? { rowTotalValue: totalCv, rowTotalHeaders } : {}),
    ...(Object.keys(yearScopes).length ? { yearScopes } : {}),
  };
}

/** "2024 Net Sales" → "Net Sales" when "2024" is one of the pivot column
 *  values; unchanged otherwise. Exact prefix match (no substring guessing). */
export function stripColumnValue(key: string, columnValues: string[]): string {
  for (const cv of columnValues || []) {
    const prefix = `${cv || '(blank)'} `;
    if (key.startsWith(prefix)) return key.slice(prefix.length);
  }
  return key;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Formulas on an EXISTING tree (backend-computed trees + live re-evaluation)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Evaluate formula columns on every node of a tree (and on the grand total) IN
 * PLACE. Works on both node shapes: the backend's (values under `measures`)
 * and lib-drill-down's flattened clone (values as top-level props too — both
 * are updated when present). With pivot `columnValues`, each formula is
 * evaluated per column value on that value's measure slice.
 */
export function applyDrillFormulasToTree(
  nodes: Array<Record<string, unknown>> | null | undefined,
  total: Record<string, unknown> | null | undefined,
  meta: DrillFormulaMeta | null | undefined,
  evaluatorFactory: RowEvaluatorFactory,
): void {
  const computed = (meta?.computedColumns || []).filter((c) => c && c.name && c.expression);
  if (!computed.length) return;
  const bases = (meta?.baseMeasureKeys || []).filter(Boolean);
  const columnValues = meta?.columnValues || [];
  const hasPivot = columnValues.length > 0;
  const unsplit = new Set(meta?.unsplitKeys || []);
  const aliases = meta?.aliases || {};
  const aliasNames = Object.keys(aliases).filter((a) => a && !bases.includes(a));
  const compiled = compileComputed(computed, [...bases, ...aliasNames], evaluatorFactory);
  if (!compiled.length) return;
  const withAliases = (bag: Record<string, unknown>): Record<string, unknown> => {
    aliasNames.forEach((a) => { bag[a] = bag[aliases[a]]; });
    return bag;
  };
  // A formula is per-column-value unless the tree builder marked it unsplit.
  const isSplitFormula = (c: { name: string; split?: boolean }): boolean => hasPivot && !unsplit.has(c.name) && c.split !== false;
  const splitCompiled = compiled.filter((c) => isSplitFormula(c));
  const unsplitCompiled = compiled.filter((c) => !isSplitFormula(c));

  const readValue = (node: Record<string, unknown>, key: string): unknown => {
    const m = node['measures'];
    if (m && typeof m === 'object' && key in (m as Record<string, unknown>)) return (m as Record<string, unknown>)[key];
    return node[key];
  };
  const write = (node: Record<string, unknown>, key: string, value: unknown): void => {
    node[key] = value;
    const m = node['measures'];
    if (m && typeof m === 'object') (m as Record<string, unknown>)[key] = value;
  };
  const anyPresent = (bag: Record<string, unknown>): boolean => bases.some((b) => bag[b] !== undefined && bag[b] !== null);

  const applyTo = (node: Record<string, unknown>, isTotal: boolean): void => {
    // Whole-node values: unsplit measures live under their base key; with a
    // pivot the split measures are only present per column value.
    const baseBag: Record<string, unknown> = {};
    bases.forEach((base) => { baseBag[base] = readValue(node, base); });
    if (unsplitCompiled.length && (!isTotal || anyPresent(baseBag))) {
      unsplitCompiled.forEach((c) => {
        const out: Record<string, unknown> = {};
        applyComputedToBag([c], withAliases({ ...baseBag }), out, (b) => b);
        Object.keys(out).forEach((k) => write(node, k, out[k]));
        baseBag[c.name] = out[c.name];
      });
    }
    if (!splitCompiled.length) return;
    columnValues.forEach((cv) => {
      const keyFor = (base: string): string => pivotedKey(cv, base);
      const bag: Record<string, unknown> = { ...baseBag };
      bases.forEach((base) => { bag[base] = readValue(node, unsplit.has(base) ? base : keyFor(base)); });
      // Totals only carry the measures flagged for the grand total.
      if (isTotal && !anyPresent(bag)) return;
      splitCompiled.forEach((c) => {
        const out: Record<string, unknown> = {};
        applyComputedToBag([c], withAliases({ ...bag }), out, keyFor);
        Object.keys(out).forEach((k) => write(node, k, out[k]));
        bag[c.name] = out[keyFor(c.name)];
      });
    });
  };
  const walk = (list: Array<Record<string, unknown>>): void => {
    (list || []).forEach((node) => {
      if (!node || node['isGrandTotal'] || node['isTotal']) return;
      applyTo(node, false);
      if (Array.isArray(node['children'])) walk(node['children'] as Array<Record<string, unknown>>);
    });
  };
  walk(nodes || []);
  if (total && typeof total === 'object') applyTo(total, true);
}


/** Convenience: the tree as a compute-response-shaped envelope, so hosts can
 *  feed it to their existing `buildDrillDownWidgetData(response)` untouched. */
export function drillTreeToResponse(tree: DrillClientTree): Record<string, unknown> {
  return {
    data: tree.data,
    total: tree.total,
    // Pivot/formula metadata the widget builder needs (column values for the
    // editable-key match, base keys for live formula re-evaluation).
    drillMeta: {
      baseMeasureKeys: tree.baseMeasureKeys,
      columnValues: tree.columnValues,
      measureKeys: tree.measureKeys,
      textMeasureKeys: tree.textMeasureKeys,
      unsplitKeys: tree.unsplitKeys,
      computed: tree.computed,
      // Which label is the spanning band: the measure (default) or the column value.
      headerGroupBy: tree.headerGroupBy,
      // Row-total block: its column value + per-measure header renames.
      ...(tree.rowTotalValue ? { rowTotalValue: tree.rowTotalValue, rowTotalHeaders: tree.rowTotalHeaders ?? {} } : {}),
      // Year-scoped measures: base key → the year shown (header info text).
      ...(tree.yearScopes ? { yearScopes: tree.yearScopes } : {}),
    },
  };
}

/**
 * RENDER-TIME header re-layout for an already-built drill-down widget `data`
 * (the builder's tile / a published widget's baked `drilldownData`): re-orders
 * the pivoted headers and swaps which label is the spanning band so the table
 * shows `mode` WITHOUT rebuilding the tree —
 *  - 'column': "Mining" over Sales | Price % | Reason, then "Rail" over …
 *  - 'measure': "Sales" over Mining | Rail | Marine, then "Price %" over …
 * Only the header metadata moves (row values stay keyed by `prop`), so
 * editing, totals, formulas and the year filter are untouched. Non-pivoted
 * headers listed before the first pivoted one lead, the rest trail. Returns
 * the SAME object when nothing changes (hosts memoize on identity).
 */
export function applyDrillHeaderGrouping(
  data: Record<string, unknown> | null | undefined,
  mode: 'measure' | 'column',
): Record<string, unknown> | null | undefined {
  const headers = data?.['headers'];
  if (!Array.isArray(headers) || !headers.length) return data;
  type Header = Record<string, unknown>;
  // A pivoted header carries `group` + `fullName`; its prop is "<cv> <base>",
  // which tells us which of name/group currently holds the column value.
  // A year-scoped header shows its base as "Sales (2026)" (`yearLabel`); the
  // suffix is display-only, so strip it before matching against the key.
  const yearOf = (h: Header): number => { const y = Number(h['yearLabel']); return Number.isFinite(y) && y > 0 ? y : 0; };
  const stripYear = (label: string, year: number): string => (year && label.endsWith(` (${year})`) ? label.slice(0, label.length - ` (${year})`.length) : label);
  const parsed = (headers as Header[]).map((h): { cv: string; base: string } | null => {
    if (!h || h['expandabile'] || !h['group']) return null;
    const year = yearOf(h);
    const name = stripYear(String(h['name'] ?? ''), year);
    const group = stripYear(String(h['group'] ?? ''), year);
    const prop = String(h['prop'] ?? '').toLowerCase();
    // Case-insensitive: a host may have display-cased name/group ("Oil & gas").
    if (prop === `${name} ${group}`.toLowerCase()) return { cv: name, base: group };
    if (prop === `${group} ${name}`.toLowerCase()) return { cv: group, base: name };
    // Fallback: the band label is one side of the key — the other side is the
    // rest of the key (covers a renamed / re-cased `name`).
    const key = String(h['fullName'] ?? h['prop'] ?? '');
    const g = group.toLowerCase();
    if (key.toLowerCase().endsWith(` ${g}`)) return { cv: key.slice(0, key.length - group.length - 1), base: group };
    if (key.toLowerCase().startsWith(`${g} `)) return { cv: group, base: key.slice(group.length + 1) };
    return null;
  });
  const firstPivot = parsed.findIndex((p) => !!p);
  if (firstPivot < 0) return data;
  const outerOf = (p: { cv: string; base: string }): string => (mode === 'column' ? p.cv : p.base);
  const innerOf = (p: { cv: string; base: string }): string => (mode === 'column' ? p.base : p.cv);
  // Row-total block headers are stamped `rowTotal` by the widget builder. With
  // the block on the LEFT (rowTotalPosition 'left') they precede the leading
  // unsplit attributes, so the leading/trailing split is taken at the first
  // NON-total pivot header and, in the column-major layout, the block is put
  // back in front of the leading columns — the same order the tree built.
  const isRowTotalHeader = (i: number): boolean => (headers[i] as Header)?.['rowTotal'] === true;
  const firstBodyPivot = parsed.findIndex((p, i) => !!p && !isRowTotalHeader(i));
  const boundary = firstBodyPivot < 0 ? firstPivot : firstBodyPivot;
  const totalBlockLeads = mode === 'column' && isRowTotalHeader(firstPivot) && firstPivot < boundary;
  const leading: number[] = [];
  const trailing: number[] = [];
  const pivots: number[] = [];
  headers.forEach((_h, i) => {
    if (parsed[i]) pivots.push(i);
    else if (i < boundary) leading.push(i);
    else trailing.push(i);
  });
  const outers = Array.from(new Set(pivots.map((i) => outerOf(parsed[i]!))));
  const inners = Array.from(new Set(pivots.map((i) => innerOf(parsed[i]!))));
  const ordered: number[] = [];
  outers.forEach((outer) => {
    inners.forEach((inner) => {
      const hit = pivots.find((i) => outerOf(parsed[i]!) === outer && innerOf(parsed[i]!) === inner);
      if (hit !== undefined) ordered.push(hit);
    });
  });
  const totalBlock = totalBlockLeads ? ordered.filter(isRowTotalHeader) : [];
  const body = totalBlockLeads ? ordered.filter((i) => !isRowTotalHeader(i)) : ordered;
  // The tree column (anything before the first pivot header) always stays
  // first; a leading Total block follows it, then the leading attributes.
  const pre = leading.filter((i) => i < firstPivot);
  const mid = leading.filter((i) => i > firstPivot);
  const next = [...pre, ...totalBlock, ...mid, ...body, ...trailing].map((i) => {
    const p = parsed[i];
    const h = headers[i] as Header;
    if (!p) return h;
    // A row-total column renamed for display ("Revenue" over the summed Sales)
    // keeps that text as its sub-header in the column-major layout; its band
    // in the measure-major layout is still the measure (the key's base).
    const renamed = typeof h['rowTotalHeader'] === 'string' && h['rowTotalHeader'] ? String(h['rowTotalHeader']) : '';
    // The year suffix follows the BASE label: the sub-header in the
    // column-major layout, the band in the measure-major one.
    const year = yearOf(h);
    const withYear = (label: string): string => (year ? `${label} (${year})` : label);
    const name = mode === 'column' ? withYear(renamed || innerOf(p)) : innerOf(p);
    const group = mode === 'column' ? outerOf(p) : withYear(outerOf(p));
    return h['name'] === name && h['group'] === group ? h : { ...h, name, group };
  });
  const unchanged = next.length === headers.length && next.every((h, i) => h === headers[i]);
  return unchanged ? data : { ...data, headers: next };
}

/** The formula-metadata block lib-drill-down reads (`data.drillFormulas`). */
export function drillFormulaMetaFor(
  tree: Pick<DrillClientTree, 'baseMeasureKeys' | 'columnValues'> & Partial<Pick<DrillClientTree, 'unsplitKeys' | 'computed'>>,
  measures: Array<{ field: string; label?: string }>,
  computedColumns: DrillClientComputedColumn[],
): DrillFormulaMeta {
  const aliases: Record<string, string> = {};
  measures.forEach((m) => { if (m.field) aliases[m.field] = drillMeasureKey(m); });
  return {
    computedColumns: (tree.computed ?? computedColumns).map((c) => ({ ...c })),
    baseMeasureKeys: [...tree.baseMeasureKeys],
    columnValues: [...tree.columnValues],
    aliases,
    ...(tree.unsplitKeys ? { unsplitKeys: [...tree.unsplitKeys] } : {}),
  };
}
